/**
 * A game from title to game over (spec 08 §1): the title waits for the menu; a new game from the
 * title shows the C64's story screen, then level 1; play steps the simulation one 50 Hz frame at a
 * time; a level's end scrolls on - to the next level, past the levels an umbrella or the white door
 * skips, into the secret room and out - in the C64's 32 frames a level (`levelExits.ts`); level 100
 * won is the ending; when every player is out, GAME OVER, then the title again. Screen changes other
 * than the scroll pass through a short transition (`Transition`). Holds the high score.
 */

import { level } from '../../levels';
import { SECRET_ROOM, SECRET_EXIT_COLS } from '../../levels/secretRoom';
import type { LevelDef } from '../../levels/types';
import { boltLooks } from '../../sim/bubbles';
import { setupSecret } from '../../sim/secret';
import type { Command, World } from '../../sim/types';
import { createWorld, nextLevelWorld, step } from '../../sim/world';
import { type Previous, snapshot } from '../../render/game/entityViews';
import type { GameView } from '../../render/game/GameView';
import { HiScore } from './hiScore';
import { planExit } from './levelExits';
import type { MusicDirector } from './music';
import { Transition } from './transition';
import { STORY_FRAMES } from '../../render/title';

/** The C64's scroll between levels: 32 frames (spec 02 §9.1). */
const SCROLL_FRAMES = 32;
/** GAME OVER stays up this long (02 §9.2: 3 s). */
const GAME_OVER_FRAMES = 150;
/** How long a game with everyone out waits for a continue. */
const CONTINUE_FRAMES = 6 * 50;
/** The ending ignores fire this long, so a button still held from the win does not skip it. */
const ENDING_HOLD_FRAMES = 150;
/** How soon fire may skip the story screen (the C64 holds it `STORY_FRAMES`, 5.1 s; it could not be skipped). */
const STORY_HOLD_FRAMES = 50;

export type Mode = 'title' | 'story' | 'play' | 'scroll' | 'over' | 'ending';

export class Session {
  mode: Mode = 'title';
  /** Paused (P, Esc, START; the C64 used C=): nothing runs, the picture holds. */
  paused = false;
  /** The SUPORR chord was held on the title (02 §9.5): the next game swaps its monsters. */
  suporr = false;
  /** Played below full speed at some point: the high score is not kept for this game (08 §4). */
  practice = false;
  world: World | null = null;
  readonly prev: Previous = new Map();
  /** Frames since the mode began. */
  timer = 0;
  readonly hi = new HiScore();
  readonly transition = new Transition();
  private seed = 1;
  hurryShown = -1;
  extendPlayer = 0;
  private outFrames = 0;
  private players: [boolean, boolean] = [true, false];
  /** The levels still to scroll through, the current target first. */
  private path: LevelDef[] = [];
  private secretFrom = -1;
  private secretOpen = false;
  /** Level 100 was won: the title's round reads ALL. */
  completed = false;

  constructor(private readonly view: GameView, private readonly music: MusicDirector, readonly startLevel = 1) {
    view.showBackdrop('title');
  }

  /**
   * A new game: from the title, through the story screen when it starts on level 1 (`story`); a URL
   * can start one at once on any level.
   */
  start(players: [boolean, boolean], story = false): void {
    if (this.transition.active) return;
    this.players = players;
    this.completed = false;
    this.practice = false;
    this.music.gameStart();
    if (story && this.startLevel === 1) {
      this.transition.run(() => {
        this.mode = 'story';
        this.timer = 0;
        this.view.showBackdrop('story');
      });
    } else if (story) {
      this.transition.run(() => this.begin());
    } else {
      this.begin();
    }
  }

  /** Play begins: the first level's world. */
  private begin(): void {
    const def = level(this.startLevel);
    this.seed = (Math.random() * 0xffffffff) >>> 0;
    this.world = createWorld(def, { players: this.players, seed: this.seed, options: { suporr: this.suporr } });
    this.outFrames = 0;
    this.hurryShown = -1;
    this.prev.clear();
    this.view.showBackdrop(null);
    this.view.resetFigures();
    this.view.setLevel(def);
    this.view.hideFigures = false;
    this.mode = 'play';
    this.timer = 0;
    if (def.n === 100) this.music.levelStart(100);
  }

  /** Q on the C64: back to the title, the game abandoned. */
  quit(): void {
    this.paused = false;
    if (this.mode !== 'title') this.transition.run(() => this.toTitle());
  }

  /** One 50 Hz frame; `force` runs it even while paused (the test hook `__game.advance`). */
  tick(commands: readonly [Command, Command], force = false): void {
    if (this.paused && !force) return;
    this.transition.tick();
    this.timer++;
    if (this.transition.holding) return;
    if (this.mode === 'title') return;
    if (this.mode === 'story') {
      const skip = this.timer >= STORY_HOLD_FRAMES && (commands[0].fire || commands[1].fire);
      if ((this.timer >= STORY_FRAMES || skip) && !this.transition.active) this.transition.run(() => this.begin());
      return;
    }
    if (this.mode === 'ending') {
      if (this.timer >= ENDING_HOLD_FRAMES && (commands[0].fire || commands[1].fire) && !this.transition.active) {
        this.transition.run(() => this.toTitle());
      }
      return;
    }
    const w = this.world;
    if (!w) return;
    if (this.mode === 'scroll') return this.scrolling(w);
    if (this.mode === 'over') {
      if (this.timer >= GAME_OVER_FRAMES && !this.transition.active) this.transition.run(() => this.toTitle());
      return;
    }
    snapshot(w, this.prev);
    // What the effects need to know that the events do not say: the kind of an item before it is
    // taken, and whether a red-cross shot is in flight (its kills are reported as 'other').
    const kinds = new Map(w.items.map((i) => [i.id, i.kind]));
    const redCross = boltLooks(w).some((b) => b.redCross);
    step(w, commands);
    this.music.onEvents(w.events, {
      itemKind: (id) => kinds.get(id), redCross: redCross || boltLooks(w).some((b) => b.redCross),
    });
    for (const e of w.events) {
      if (e.type === 'hurryUp') this.hurryShown = w.flow.frame;
      if (e.type === 'extend') this.extendPlayer = e.player;
    }
    if (!this.practice) for (const p of w.players) this.hi.offer(p.score);
    if (w.secret?.open && !this.secretOpen) this.openSecretExit();
    // Everyone out of lives: a few seconds for someone to continue with fire (02 §3), then GAME OVER.
    const everyoneOut = w.players.every((p) => p.state === 'out' || p.state === 'gameOver');
    this.outFrames = everyoneOut ? this.outFrames + 1 : 0;
    if (everyoneOut && (w.continues <= 0 || this.outFrames >= CONTINUE_FRAMES)) return this.over();
    if (w.flow.phase === 'done') this.levelOver(w);
  }

  /** The level is over: scroll on along the exit's path, or go to the ending. */
  private levelOver(w: World): void {
    const plan = planExit(w.level.n, w.flow.exit);
    const first = plan.kind === 'scroll' ? plan.path[0] : undefined;
    if (plan.kind === 'ending' || !first) {
      this.mode = 'ending';
      this.timer = 0;
      this.transition.run(() => this.toEnding());
      return;
    }
    this.path = plan.path;
    this.secretFrom = plan.secretFrom;
    this.view.beginScroll(first);
    this.view.hideFigures = true;
    this.mode = 'scroll';
    this.timer = 0;
  }

  private scrolling(w: World): void {
    this.view.scroll(Math.min(1, this.timer / SCROLL_FRAMES));
    if (this.timer < SCROLL_FRAMES) return;
    const [here, ...rest] = this.path;
    if (!here) return;
    this.view.endScroll(here);
    const next = rest[0];
    if (next) {
      // Skipping: each level passes by in turn.
      this.path = rest;
      this.view.beginScroll(next);
      this.timer = 0;
      return;
    }
    this.seed = (this.seed * 1103515245 + 12345) >>> 0;
    this.world = nextLevelWorld(w, here, this.seed);
    if (here === SECRET_ROOM) setupSecret(this.world, this.secretFrom);
    this.secretOpen = false;
    this.view.resetFigures();
    this.view.hideFigures = false;
    this.prev.clear();
    this.hurryShown = -1;
    this.mode = 'play';
    this.music.levelStart(here.n);
    this.hi.save();
  }

  /** The secret room's floor exit has opened: the stage loses those two floor blocks. */
  private openSecretExit(): void {
    this.secretOpen = true;
    const bottom = SECRET_ROOM.tiles.length - 1;
    const tiles = SECRET_ROOM.tiles.map((row, r) => (r !== bottom ? row
      : [...row].map((ch, c) => (SECRET_EXIT_COLS.includes(c) ? '.' : ch)).join('')));
    this.view.setLevel({ ...SECRET_ROOM, tiles });
  }

  private toEnding(): void {
    this.mode = 'ending';
    this.timer = 0;
    this.completed = true;
    this.view.hideFigures = true;
    this.view.showBackdrop('ending');
    this.music.ending();
    this.hi.save();
  }

  private over(): void {
    this.mode = 'over';
    this.timer = 0;
    this.music.gameOver();
    this.hi.save();
  }

  private toTitle(): void {
    this.mode = 'title';
    this.world = null;
    this.paused = false;
    this.view.resetFigures();
    this.view.setLevel(level(this.startLevel));
    this.view.showBackdrop('title');
    this.timer = 0;
    this.music.title();
    this.hi.save();
  }
}
